// 里（一枚絵の施設一覧）と建物図面。施設は銭＋材木でLvアップ。時間で貯まる分は24時間で止まる。
import type { BuildingType } from '../types';
import { BUILDING_TYPES, LATE_BUILDING_TYPES } from '../types';
import { CONFIG } from './config';
import { conqueredDistrictCount, hardUnlocked } from './provinces';
import { FACILITY_IDS, type Bundle, type FacilityId, type OpResult, type Profile, type Resources } from './profile';

export interface FacilityDef {
  id: FacilityId;
  name: string;
  desc: string;
}

export const VILLAGE: Record<FacilityId, FacilityDef> = {
  kura: { id: 'kura', name: '蔵', desc: '銭が時間で貯まる' },
  hokora: { id: 'hokora', name: '祠', desc: '霊玉が少しずつ貯まる' },
  seizaisho: { id: 'seizaisho', name: '製材所', desc: '材木が時間で貯まる' },
  hyorogura: { id: 'hyorogura', name: '兵糧庫', desc: '兵糧の最大値と予備の上限が上がる' },
  dojo: { id: 'dojo', name: '道場', desc: 'あるじのEXPが時間で少し貯まる' },
  kajiya: { id: 'kajiya', name: '鍛冶屋', desc: '装備の強化の上限（限界突破）と、建物図面の上限が開く' },
  yashiro: { id: 'yashiro', name: '式神の社', desc: '式神の勧誘率が上がる' },
};

/** 1時間あたりの生産量 */
export function villageRates(p: Profile): { zeni: number; reidama: number; zaimoku: number; exp: number } {
  const v = CONFIG.village;
  const L = p.village.levels;
  return {
    zeni: L.kura * v.kuraZeniPerHour,
    reidama: L.hokora * v.hokoraReidamaPerHour,
    zaimoku: L.seizaisho * v.seizaiZaimokuPerHour,
    exp: L.dojo * v.dojoExpPerHour,
  };
}

/** 受け取れる量（最後の受け取りから最大24時間分）。profile は書き換えない */
export function villagePending(p: Profile, now: number): { zeni: number; reidama: number; zaimoku: number; exp: number; hours: number } {
  const hours = Math.max(0, Math.min(CONFIG.village.capHours, (now - p.village.lastCollect) / 3_600_000));
  const r = villageRates(p);
  return {
    zeni: Math.floor(r.zeni * hours),
    reidama: Math.floor(r.reidama * hours),
    zaimoku: Math.floor(r.zaimoku * hours),
    exp: Math.floor(r.exp * hours),
    hours,
  };
}

/**
 * 受け取り。銭・霊玉・材木は res に加え、EXP は返り値で返す（主EXPは gainExp で加える：Lvアップ処理のため）。
 * 最後の受け取り時刻を now にする。
 */
export function collectVillage(p: Profile, now: number): { zeni: number; reidama: number; zaimoku: number; exp: number; hours: number } {
  const g = villagePending(p, now);
  p.village.lastCollect = now;
  return g;
}

/** 施設Lvの上限。国盗りで地方を制圧するほど上がる */
export const facilityMaxLevel = (p: Profile) =>
  Math.min(CONFIG.village.maxLevel, CONFIG.village.levelBase + CONFIG.village.levelPerProvince * conqueredDistrictCount(p));

export function facilityUpgradeCost(id: FacilityId, level: number): Bundle {
  const v = CONFIG.village;
  const next = level + 1;
  // 祠と社は少し高い
  const k = id === 'hokora' || id === 'yashiro' ? 1.5 : 1;
  return {
    zeni: Math.round(v.upgradeZeniBase * Math.pow(next, v.upgradeZeniExp) * k),
    zaimoku: Math.round(v.upgradeZaimokuBase * Math.pow(next, v.upgradeZaimokuExp) * k),
  };
}

/** 施設のLvアップ。時間で貯まる施設は先に collectVillage しておくこと（生産量が変わるため） */
export function upgradeFacility(p: Profile, res: Resources, id: FacilityId): OpResult<{ level: number; cost: Bundle }> {
  if (!FACILITY_IDS.includes(id)) return { ok: false, error: 'unknown' };
  const lv = p.village.levels[id];
  if (lv >= facilityMaxLevel(p)) return { ok: false, error: 'level_cap' };
  const cost = facilityUpgradeCost(id, lv);
  if (res.zeni < cost.zeni! || res.zaimoku < cost.zaimoku!) return { ok: false, error: 'resources' };
  res.zeni -= cost.zeni!;
  res.zaimoku -= cost.zaimoku!;
  p.village.levels[id] = lv + 1;
  return { ok: true, level: lv + 1, cost };
}

// ---- 建物図面（材木＋銭で建物ごとのLv。戦闘には loadout.build.levels で渡す） ----

export function blueprintCost(level: number): Bundle {
  const b = CONFIG.blueprint;
  const next = level + 1;
  return { zaimoku: Math.round(b.zaimokuBase * Math.pow(next, b.zaimokuExp)), zeni: Math.round(b.zeniBase * Math.pow(next, b.zeniExp)) };
}

/** 図面の Lv の上限：里の鍛冶屋の Lv まで（2026-11-04。BALANCE.md 54章） */
export const blueprintCap = (p: Profile) => Math.min(CONFIG.blueprint.maxLevel, p.village.levels.kajiya ?? 0);

export function upgradeBlueprint(p: Profile, res: Resources, type: BuildingType): OpResult<{ level: number; cost: Bundle }> {
  if (!BUILDING_TYPES.includes(type)) return { ok: false, error: 'unknown' };
  const lv = p.blueprints[type] ?? 0;
  if (lv >= CONFIG.blueprint.maxLevel) return { ok: false, error: 'level_cap' };
  if (lv >= blueprintCap(p)) return { ok: false, error: 'kajiya_level' };
  // 鉄砲狭間などは本編（第100陣）クリア後に図面を作れる
  if (LATE_BUILDING_TYPES.includes(type) && !hardUnlocked(p)) return { ok: false, error: 'after_clear' };
  const cost = blueprintCost(lv);
  if (res.zeni < cost.zeni! || res.zaimoku < cost.zaimoku!) return { ok: false, error: 'resources' };
  res.zeni -= cost.zeni!;
  res.zaimoku -= cost.zaimoku!;
  p.blueprints[type] = lv + 1;
  return { ok: true, level: lv + 1, cost };
}
